'use client'

import { useEffect, useState } from 'react'
import { QRCode } from './qr-code'
import { QRCodeGreen } from './qr-code-green'
import { env } from '~/env'

type ShareQRProps = {
  /** Path only, e.g. `/events/abc` — the origin is resolved at render. */
  path: string
  size?: number
  caption?: string | null
  /** Render the green (check-in-styled) tile that links to the ticket page. */
  green?: boolean
  eventId?: string
  /** Offer a "Download QR Code" button (facilitators/admins only). */
  downloadable?: boolean
  /** Basename for the downloaded PNG, without extension. */
  downloadFileName?: string
}

/**
 * Renders a share QR that encodes an ABSOLUTE url. The domain is resolved from
 * `window.location.origin` at render time rather than a build-time env var:
 * `NEXT_PUBLIC_WEB_URL` was never wired into the CI build args, so every share
 * QR previously encoded a bare `/events/…` path with no host and opened
 * nothing. Origin-at-render is also correct across dev / QA / prod with zero
 * per-environment config.
 */
export function ShareQR({
  path,
  size = 128,
  caption,
  green,
  eventId,
  downloadable,
  downloadFileName,
}: ShareQRProps) {
  const [origin, setOrigin] = useState('')
  useEffect(() => {
    setOrigin(window.location.origin)
  }, [])

  const base = origin || env.NEXT_PUBLIC_WEB_URL || ''
  if (!base) {
    // Pre-hydration (or the rare misconfig): reserve the space, don't render a
    // domain-less code someone might scan.
    return <div style={{ width: size, height: size }} aria-hidden />
  }

  const value = `${base}${path}`
  if (green) {
    return (
      <QRCodeGreen
        value={value}
        size={size}
        eventId={eventId}
        caption={caption ?? undefined}
      />
    )
  }
  return caption === undefined ? (
    <QRCode
      value={value}
      size={size}
      downloadable={downloadable}
      downloadFileName={downloadFileName}
    />
  ) : (
    <QRCode
      value={value}
      size={size}
      caption={caption}
      downloadable={downloadable}
      downloadFileName={downloadFileName}
    />
  )
}
